/**
 * One agent result -> the Lite store, as ONE undo step: packages/agent applyOps computes the next component list
 * from the current one, the store swaps it in with a single commit(), `select` filters are evaluated by the data
 * worker into the selection (same transaction, no second commit), background changes go to the view, and `focus`
 * asks the engine to frame the target once the new geometry has had a moment to build.
 *
 * The skybox is view state and not part of the undo snapshot, so a background change is not undone (the same holds
 * for the settings popup).
 */
import { nextTick, toRaw } from 'vue';
import { applyOps } from '@nanome-lite/agent/apply';
import type { AgentResult, UnsupportedOp } from '@nanome-lite/agent/ops';
import type { LiteStore } from '@nanome-lite/state';

export interface AppliedResult {
  /** One line per applied op (applyOps description). */
  description: string[];
  unsupported: UnsupportedOp[];
  /** True when the store took an undo snapshot. */
  committed: boolean;
  /** Atoms selected by a `select` op, when there was one. */
  selectedAtoms?: number;
  focused: boolean;
}

const FOCUS_DELAY_MS = 250;

export async function applyAgentResult(store: LiteStore, result: AgentResult, opts: { focusDelayMs?: number } = {}): Promise<AppliedResult> {
  const before = store.components.map(c => toRaw(c));
  const view = { skyboxTheme: store.view.skyboxTheme, nightMode: store.view.nightMode };
  const r = applyOps(result.ops, { components: JSON.parse(JSON.stringify(before)), nextComponentSerial: store.peekNextComponentSerial(), view });

  const componentsChanged = JSON.stringify(r.components) !== JSON.stringify(before);
  let committed = false;
  if (componentsChanged) {
    store.replaceComponents(r.components, { nextComponentSerial: r.nextComponentSerial });
    committed = true;
  }

  if (r.view && (r.view.skyboxTheme !== view.skyboxTheme || r.view.nightMode !== view.nightMode)) store.setSkybox(r.view);

  const out: AppliedResult = { description: r.description, unsupported: r.unsupported, committed, focused: false };

  if (r.select) {
    try {
      const byEntry = await store.evaluateFilterAtoms(r.select);
      store.replaceSelection(byEntry, { commit: !committed });
      out.committed = true;
      out.selectedAtoms = Object.values(byEntry).reduce((n, a) => n + a.length, 0);
    }
    catch (e) {
      store.toast('warn', 'Could not select those atoms', e instanceof Error ? e.message : String(e));
    }
  }

  if (r.focus) {
    const target = r.focus;
    out.focused = true;
    await nextTick();
    const run = () => {
      if (typeof target.componentSerial === 'number') store.zoomComponent(target.componentSerial);
      else if (typeof target.entrySerial === 'number') store.zoomEntry(target.entrySerial);
    };
    const delay = opts.focusDelayMs ?? FOCUS_DELAY_MS;
    if (delay > 0) setTimeout(run, delay); else run();
  }

  return out;
}
